import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toolsAPI } from '../lib/api';

const TOOL_PATHS = {
  'standings-updater': '/tools/standings-updater',
  'athlete-importer': '/tools/athlete-importer',
  'series-standings': '/tools/series-standings',
  'vmix-script-editor': '/tools/vmix-script-editor',
  'climb-bridge': '/tools/climb-bridge',
  'timing-bridge': '/tools/timing-bridge',
  'preview-sync': '/tools/preview-sync',
};

function formatRelative(dt) {
  if (!dt) return null;
  const d = new Date(dt);
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString();
}

export default function ToolsIndex() {
  const navigate = useNavigate();
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    toolsAPI.list()
      .then(res => setTools(res.data.tools || []))
      .catch(err => setError(err.response?.data?.error || 'Failed to load tools'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-5xl mx-auto px-6 py-8">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-white">Tools</h1>
        <p className="text-gray-500 text-sm mt-0.5">
          Internal production utilities
        </p>
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 bg-red-900/30 border border-red-700 rounded text-red-400 text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-center py-16 text-gray-600">Loading tools...</div>
      ) : tools.length === 0 ? (
        <div className="text-center py-16 text-gray-600">No tools available</div>
      ) : (
        <div className="space-y-2">
          {tools.map(tool => (
            <div
              key={tool.key}
              onClick={() => navigate(TOOL_PATHS[tool.key] || '/tools')}
              className="flex items-center gap-4 bg-[#111111] border border-[#2a2a2a] hover:border-[#3a3a3a] rounded-lg px-5 py-4 cursor-pointer transition-colors group"
            >
              <div className="w-10 h-10 bg-[#1a1a1a] border border-[#2a2a2a] rounded flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
              </div>

              <div className="flex-1 min-w-0">
                <div className="font-medium text-white group-hover:text-blue-400 transition-colors truncate">
                  {tool.name}
                </div>
                <div className="text-xs text-gray-500 mt-0.5 truncate">
                  {tool.description}
                </div>
              </div>

              {tool.last_run_at && (
                <div className="text-xs text-gray-600 hidden sm:block">
                  Last run {formatRelative(tool.last_run_at)}
                </div>
              )}

              <svg className="w-4 h-4 text-gray-700 group-hover:text-gray-400 transition-colors flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
